export interface RoyaltyAutoAllocationItem {
  paymentId: number
  expectedUpdatedAt: string
}

export interface RoyaltyAutoAllocationSkip {
  payment_id: number | null
  reason: string
  sqlstate: string
}

export interface RoyaltyAutoAllocationBatchResult {
  executed_count: number
  skipped_count: number
  skipped: RoyaltyAutoAllocationSkip[]
}

export type RoyaltyAutoAllocationPhase =
  | 'PREPARING'
  | 'RUNNING'
  | 'RETRYING'
  | 'FAILED'
  | 'COMPLETED'

export interface RoyaltyAutoAllocationProgress {
  phase: RoyaltyAutoAllocationPhase
  initialReady: number
  attempted: number
  allocated: number
  skipped: number
  remainingReady: number | null
  batchNumber: number
  batchSize: number
  message: string | null
}

export interface RoyaltyAutoAllocationResult {
  allocated: number
  attempted: number
  batches: number
  initialReady: number
  remainingReady: number
  skipped: RoyaltyAutoAllocationSkip[]
}

interface RunRoyaltyAutoAllocationInput {
  initialReady: number
  batchSize?: number
  maxPayments?: number
  loadItems: (
    attemptedPaymentIds: ReadonlySet<number>,
    maxItems: number
  ) => Promise<RoyaltyAutoAllocationItem[]>
  executeItems: (
    items: RoyaltyAutoAllocationItem[]
  ) => Promise<RoyaltyAutoAllocationBatchResult>
  loadRemainingReady: () => Promise<number>
  onProgress?: (progress: RoyaltyAutoAllocationProgress) => void
  isTimeoutError: (error: unknown) => boolean
  errorMessage: (error: unknown) => string
}

const DEFAULT_BATCH_SIZE = 20
const DEFAULT_MAX_PAYMENTS = 10_000

function splitItems(items: RoyaltyAutoAllocationItem[]): [RoyaltyAutoAllocationItem[], RoyaltyAutoAllocationItem[]] {
  const middle = Math.ceil(items.length / 2)
  return [items.slice(0, middle), items.slice(middle)]
}

/**
 * Runs target-based royalty allocation until there are no more exact-ready
 * payments in the selected scope. Transaction-level timeouts are retried by
 * recursively splitting the batch; per-payment business rejections returned by
 * the RPC are recorded and do not stop later payments.
 */
export async function runRoyaltyAutoAllocation(
  input: RunRoyaltyAutoAllocationInput
): Promise<RoyaltyAutoAllocationResult> {
  const configuredBatchSize = Math.max(1, Math.floor(input.batchSize ?? DEFAULT_BATCH_SIZE))
  const maxPayments = Math.max(configuredBatchSize, Math.floor(input.maxPayments ?? DEFAULT_MAX_PAYMENTS))
  const attemptedPaymentIds = new Set<number>()
  const skipped: RoyaltyAutoAllocationSkip[] = []
  let allocated = 0
  let batches = 0

  const emit = (
    phase: RoyaltyAutoAllocationPhase,
    batchSize: number,
    message: string | null = null,
    remainingReady: number | null = null
  ) => {
    input.onProgress?.({
      phase,
      initialReady: input.initialReady,
      attempted: attemptedPaymentIds.size,
      allocated,
      skipped: skipped.length,
      remainingReady,
      batchNumber: batches,
      batchSize,
      message,
    })
  }

  const executeAdaptive = async (items: RoyaltyAutoAllocationItem[]): Promise<void> => {
    if (items.length === 0) return

    try {
      batches += 1
      emit('RUNNING', items.length)
      const result = await input.executeItems(items)

      for (const item of items) attemptedPaymentIds.add(item.paymentId)
      allocated += Number(result.executed_count ?? 0)
      skipped.push(...(result.skipped ?? []))
      emit('RUNNING', items.length)
    } catch (error) {
      if (!input.isTimeoutError(error)) throw error

      const message = input.errorMessage(error)
      if (items.length === 1) {
        const item = items[0]
        attemptedPaymentIds.add(item.paymentId)
        skipped.push({
          payment_id: item.paymentId,
          reason: `Авторазнос не уложился в лимит времени: ${message}`,
          sqlstate: '57014',
        })
        emit('RETRYING', 1, `Платёж #${item.paymentId} пропущен после таймаута`)
        return
      }

      const [left, right] = splitItems(items)
      emit(
        'RETRYING',
        left.length,
        `Пачка из ${items.length} не уложилась в лимит — повторяем частями ${left.length} + ${right.length}`
      )
      await executeAdaptive(left)
      await executeAdaptive(right)
    }
  }

  emit('PREPARING', configuredBatchSize)

  while (attemptedPaymentIds.size < maxPayments) {
    const room = maxPayments - attemptedPaymentIds.size
    const items = await input.loadItems(
      attemptedPaymentIds,
      Math.min(configuredBatchSize, room)
    )

    if (items.length === 0) break
    await executeAdaptive(items)
  }

  const remainingReady = await input.loadRemainingReady()
  if (remainingReady > 0 && attemptedPaymentIds.size >= maxPayments) {
    throw new Error(
      `Авторазнос достиг защитного лимита ${maxPayments} платежей; в очереди осталось ${remainingReady}`
    )
  }

  emit('COMPLETED', 0, null, remainingReady)
  return {
    allocated,
    attempted: attemptedPaymentIds.size,
    batches,
    initialReady: input.initialReady,
    remainingReady,
    skipped,
  }
}
